import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Container from "@/components/base/Container";
import FilmCard from "@/components/feature/FilmCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import { films, keywordSuggestions } from "@/mocks/films";
import { news } from "@/mocks/news";
import type { Film, NewsArticle } from "@/types/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const TABS = [
  { key: "全部", icon: "ri-apps-2-line" },
  { key: "电影", icon: "ri-film-line" },
  { key: "电视剧", icon: "ri-tv-2-line" },
  { key: "资讯", icon: "ri-newspaper-line" },
];

function matches(haystack: string[], keyword: string): boolean {
  const lower = keyword.toLowerCase();
  return haystack.some((text) => text.toLowerCase().includes(lower));
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filmList = films as Film[];
  const newsList = news as NewsArticle[];

  const keyword = searchParams.get("q") || "";
  const trimmed = keyword.trim();
  const hasQuery = trimmed.length > 0;
  const categoryParam = searchParams.get("category");
  const browsing = !hasQuery && Boolean(categoryParam);
  const tab = categoryParam || "全部";
  const [input, setInput] = useState(keyword);

  useDocumentMeta(
    hasQuery ? `搜索「${trimmed}」－夜勤病栋影视大全` : "搜索－夜勤病栋影视大全",
    "在夜勤病栋影视大全中搜索相关电影、电视剧与影视资讯。"
  );

  const filmResults = useMemo(() => {
    if (!hasQuery) return browsing ? filmList : [];
    return filmList.filter((film) =>
      matches(
        [
          film.title,
          film.originalTitle,
          film.summary,
          film.synopsis,
          film.director,
          ...film.genres,
          ...film.tags,
          ...film.cast,
          film.region,
        ],
        trimmed
      )
    );
  }, [filmList, trimmed, hasQuery, browsing]);

  const newsResults = useMemo(() => {
    if (!hasQuery) return browsing ? newsList : [];
    return newsList.filter((article) =>
      matches([article.title, article.excerpt, article.content, article.author, ...article.tags], trimmed)
    );
  }, [newsList, trimmed, hasQuery, browsing]);

  const shownFilms = tab === "资讯" ? [] : filmResults.filter((film) => tab === "全部" || film.type === tab);
  const shownNews = tab === "资讯" || tab === "全部" ? newsResults : [];
  const totalCount = shownFilms.length + shownNews.length;

  const runSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    runSearch(input);
  };

  const setTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "全部") next.delete("category");
    else next.set("category", key);
    setSearchParams(next, { replace: true });
  };

  const countLabel = hasQuery
    ? `关键词「${trimmed}」共找到 ${totalCount} 条结果`
    : `共收录 ${totalCount} 条内容`;

  return (
    <Container className="py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">搜索</h1>
      <p className="mt-2 text-sm text-foreground-500">
        输入关键词，模糊搜索夜勤病栋相关的影视作品与资讯内容。
      </p>

      <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="flex items-center gap-2 flex-1 bg-background-50 border border-background-300 rounded-md px-3 h-12 focus-within:border-primary-400 transition-colors">
          <i className="ri-search-line text-foreground-400"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、题材或关键词"
            className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
          />
          {input && (
            <button
              type="button"
              onClick={() => setInput("")}
              aria-label="清空输入"
              className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-circle-line"></i>
            </button>
          )}
        </div>
        <button
          type="submit"
          className="h-12 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          搜索
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        {keywordSuggestions.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              setInput(item);
              runSearch(item);
            }}
            className="px-3 h-8 rounded-full text-xs border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
          >
            {item}
          </button>
        ))}
      </div>

      {!hasQuery && !browsing ? (
        <div className="mt-10">
          <EmptyState
            icon="ri-search-line"
            title="开始搜索"
            description="试试输入「夜勤病栋」「医疗事故」「手术室」等关键词，或点击上方推荐词快速搜索。"
          />
        </div>
      ) : (
        <>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">{countLabel}</p>
            <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    tab === item.key
                      ? "bg-primary-600 text-background-50"
                      : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  <i className={item.icon}></i>
                  {item.key}
                </button>
              ))}
            </div>
          </div>

          {totalCount === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon="ri-emotion-sad-line"
                title={hasQuery ? `未找到与「${trimmed}」相关的内容` : "当前分类暂无内容"}
                description="请检查关键词是否正确，或尝试更换更简短的关键词，例如「夜勤」「病栋」「急诊」。"
                actionLabel="清空条件"
                onAction={() => {
                  setInput("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
              />
            </div>
          ) : (
            <div className="mt-6 space-y-10">
              {shownFilms.length > 0 && (
                <section>
                  <h2 className="text-base font-heading font-bold text-foreground-950 mb-4">
                    影视作品 · {shownFilms.length}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                    {shownFilms.map((film) => (
                      <FilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </section>
              )}

              {shownNews.length > 0 && (
                <section>
                  <h2 className="text-base font-heading font-bold text-foreground-950 mb-4">
                    资讯内容 · {shownNews.length}
                  </h2>
                  <div className="space-y-4">
                    {shownNews.map((article) => (
                      <NewsCard key={article.id} article={article} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}